import { ReactElement, useCallback } from 'react';

import {
   ConfidentialResourceAccessSelfGrantCommand,
   ConfidentialResourceType,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';
import { RequestToSummaryAccessStatus, VisitHistoryQuery } from '@dreryk/egabinet-visit-frontend-api';

import { printDocumentAsPdf, Translate, useTranslate } from '@dreryk/react-common';
import {
   dispatchSnackbarNegativeEvent,
   Dropdown,
   IconSize,
   IconWrapper,
   ShieldProtectedIcon,
   StyledFlex,
   StyledMargin,
   Text,
   TextSeparator,
   TextSeparatorType,
} from '@dreryk/react-components';
import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';

import { visitHistoryTranslationKeys } from '../../translations';

import { requestToSummaryAccessStatusVisitor } from './VisitSummaryPrintDropdownItem.utils';

const RESOURCE_TYPE: ConfidentialResourceType = 'MEDICAL_DOCUMENT';

type VisitSummaryPrintDropdownItemProps = {
   visitSummaryUrl: string;
   visitSummaryMedicalDocumentId: number;
   isMonitoredConfidentialAccessLevel: boolean;
   requestToSummaryAccessStatus: RequestToSummaryAccessStatus | undefined;
};

// eslint-disable-next-line max-lines-per-function
export const VisitSummaryPrintDropdownItem = (props: VisitSummaryPrintDropdownItemProps): ReactElement => {
   const translate = useTranslate();

   const { executeAsync: requestConfidentialAccess } = useCqrsCommand<void, void>(
      () => {
         const command = new ConfidentialResourceAccessSelfGrantCommand();
         command.resourceId = props.visitSummaryMedicalDocumentId;
         command.resourceType = RESOURCE_TYPE;
         return command;
      },
      {
         onSuccess: () => {
            refetchCqrsQuery(VisitHistoryQuery);
         },
      }
   );

   const handleClick = useCallback(
      async (hide: VoidFunction) => {
         try {
            if (props.isMonitoredConfidentialAccessLevel && !props.requestToSummaryAccessStatus) {
               await requestConfidentialAccess();
            }

            await printDocumentAsPdf({ url: props.visitSummaryUrl }).then(hide);
         } catch {
            dispatchSnackbarNegativeEvent(visitHistoryTranslationKeys.options.visitSummaryPrint.printError);
         }
      },
      [
         props.isMonitoredConfidentialAccessLevel,
         props.requestToSummaryAccessStatus,
         props.visitSummaryUrl,
         requestConfidentialAccess,
      ]
   );

   return (
      <Dropdown.Item
         testId={'visitSummaryPrint'}
         onClick={handleClick}
         disabled={requestToSummaryAccessStatusVisitor(props.requestToSummaryAccessStatus)({
            none: () => false,
            pending: () => true,
            accepted: () => false,
         })}
      >
         <StyledFlex alignItems={'center'} gap={8}>
            {props.isMonitoredConfidentialAccessLevel ? (
               <IconWrapper testId={'protectedIcon'} size={IconSize.SMALL}>
                  <ShieldProtectedIcon />
               </IconWrapper>
            ) : null}

            <StyledMargin top={1}>
               <TextSeparator separator={TextSeparatorType.SPACE}>
                  <Translate>{visitHistoryTranslationKeys.options.visitSummaryPrint.label}</Translate>
                  {props.isMonitoredConfidentialAccessLevel
                     ? requestToSummaryAccessStatusVisitor(props.requestToSummaryAccessStatus)({
                          none: () => (
                             <Text testId={'askAccess'} textTransform={'lowercase'}>
                                {`(${translate(visitHistoryTranslationKeys.request)})`}
                             </Text>
                          ),
                          pending: () => (
                             <Text testId={'requestPending'} textTransform={'lowercase'}>
                                {`(${translate(visitHistoryTranslationKeys.requestPending)})`}
                             </Text>
                          ),
                          accepted: () => null,
                       })
                     : null}
               </TextSeparator>
            </StyledMargin>
         </StyledFlex>
      </Dropdown.Item>
   );
};
